import React, { useRef, useEffect } from "react";
import { Copy, RefreshCw, Check, Sparkles, Download } from "lucide-react";
import MessageContent from "./MessageContent";
import { ModelBadge } from "./ModelSwitcher";
import { PROMPT_PRESETS, MODEL_FAMILIES } from "../constants/models";
import { toast } from "sonner";

function findModel(models, key) {
  return models.find((m) => m.key === key);
}

function AssistantAvatar({ family }) {
  const fam = MODEL_FAMILIES[family] || MODEL_FAMILIES.openai;
  return (
    <div className="h-8 w-8 shrink-0 rounded-lg flex items-center justify-center" style={{ background: fam.badgeBg, border: `1px solid ${fam.border}` }}>
      <Sparkles className="h-4 w-4" style={{ color: fam.color }} />
    </div>
  );
}

function EmptyState({ onPreset }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6" data-testid="empty-state">
      <div className="h-14 w-14 rounded-2xl bg-[#1F2421] flex items-center justify-center mb-6">
        <Sparkles className="h-7 w-7 text-white" />
      </div>
      <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#171716]">How can I help today?</h2>
      <p className="mt-2 text-[#5F5F5B] text-center max-w-md">Pick a model above and start chatting, or try one of these to get going.</p>
      <div className="mt-8 grid sm:grid-cols-2 gap-3 w-full max-w-2xl">
        {PROMPT_PRESETS.map((p) => (
          <button
            key={p.title}
            onClick={() => onPreset(p.prompt)}
            data-testid={`preset-${p.title.replace(/\s+/g, "-").toLowerCase()}`}
            className="text-left rounded-2xl border border-[#E6E6DF] bg-white p-4 hover:border-[#CECEBE] hover:-translate-y-0.5"
            style={{ transition: "transform 0.2s ease, border-color 0.2s ease" }}
          >
            <p className="font-heading font-semibold text-[#171716]">{p.title}</p>
            <p className="mt-1 text-sm text-[#5F5F5B] line-clamp-2">{p.prompt}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ChatThread({ messages, models, streaming, streamingText, streamingModel, onRegenerate, onPreset }) {
  const bottomRef = useRef(null);
  const [copiedId, setCopiedId] = React.useState(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText, streaming]);

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedId(null), 1500);
  };

  const isEmpty = messages.length === 0 && !streaming;

  if (isEmpty) {
    return (
      <div className="flex-1 overflow-y-auto omni-scroll flex" data-testid="chat-thread">
        <EmptyState onPreset={onPreset} />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto omni-scroll" data-testid="chat-thread">
      <div className="mx-auto max-w-3xl px-4 py-6 space-y-6">
        {messages.map((m) => {
          if (m.role === "user") {
            return (
              <div key={m.message_id} className="flex justify-end omni-rise" data-testid="message-user">
                <div className="max-w-[85%] rounded-2xl rounded-br-md bg-[#1F2421] text-white px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap">
                  {m.content}
                </div>
              </div>
            );
          }
          const mdl = findModel(models, m.model);
          return (
            <div key={m.message_id} className="flex gap-3 omni-rise" data-testid="message-assistant">
              <AssistantAvatar family={mdl?.family || "openai"} />
              <div className="flex-1 min-w-0">
                <div className="mb-1.5">
                  {m.model === "gemini-image"
                    ? <ModelBadge family="gemini" label="Gemini Image" />
                    : mdl && <ModelBadge family={mdl.family} label={mdl.label} />}
                </div>
                {m.image && (
                  <div className="mb-3 rounded-2xl overflow-hidden border border-[#E6E6DF] bg-white inline-block max-w-full" data-testid="generated-image">
                    <img src={m.image} alt="Generated" className="max-w-full max-h-[420px] object-contain" />
                    <a href={m.image} download="omniai-image.png" className="flex items-center gap-1.5 px-3 py-2 text-xs text-[#5F5F5B] hover:text-[#171716] border-t border-[#E6E6DF]" data-testid="download-image">
                      <Download className="h-3.5 w-3.5" /> Download
                    </a>
                  </div>
                )}
                {m.content && <MessageContent content={m.content} />}
                <div className="mt-2 flex items-center gap-1">
                  <button onClick={() => copy(m.content, m.message_id)} className="flex items-center gap-1 text-xs text-[#8C8C85] hover:text-[#171716] px-2 py-1 rounded-md hover:bg-[#EFEFEA]" style={{ transition: "background-color 0.2s ease" }} data-testid="copy-message">
                    {copiedId === m.message_id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copiedId === m.message_id ? "Copied" : "Copy"}
                  </button>
                  {onRegenerate && m.model !== "gemini-image" && (
                    <button onClick={() => onRegenerate(m)} className="flex items-center gap-1 text-xs text-[#8C8C85] hover:text-[#171716] px-2 py-1 rounded-md hover:bg-[#EFEFEA]" style={{ transition: "background-color 0.2s ease" }} data-testid="regenerate-message">
                      <RefreshCw className="h-3.5 w-3.5" /> Retry
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {streaming && (
          <div className="flex gap-3" data-testid="streaming-message">
            <AssistantAvatar family={streamingModel?.family || "openai"} />
            <div className="flex-1 min-w-0">
              {streamingModel && <div className="mb-1.5"><ModelBadge family={streamingModel.family} label={streamingModel.label} /></div>}
              {streamingText ? (
                <div className="text-[15px]"><MessageContent content={streamingText} /><span className="omni-cursor" /></div>
              ) : (
                <div className="flex gap-1.5 py-2">
                  <span className="omni-dot" />
                  <span className="omni-dot" style={{ animationDelay: "0.2s" }} />
                  <span className="omni-dot" style={{ animationDelay: "0.4s" }} />
                </div>
              )}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
